import React, { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Eye, Edit3, X } from 'react-feather';
import { DownloadSimple, ArrowUp, ArrowDown } from 'phosphor-react';
import { DillButton } from '../../../general/Dill/DillButton';
import { DillDropdown } from '../../../general/Dill/DillDropdown';
import CreditApplicationFieldsModal from './CreditApplicationFieldsModal';
import AddStepModal from './AddStepModal';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getCreditApplicationTemplate,
  updateCreditApplicationTemplate,
} from '../../../../redux/services/creditApplicationsTemplatesService';
import {
  downloadFile,
  firebaseLargeFileUploadHelper,
  getIntegrationName,
  isHTML,
  openDillToast,
  buildSupplierPath,
  toInlineHTML,
} from '../../../../utils/helpers';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import PreviewCreditApplicationTemplate from './PreviewCreditApplicationTemplate';
import { UploadFileInputs } from '../CreditApplicationComponents/UploadFileInput';
import {
  InternalFormDecisionRecipients,
  InternalFormSignerStep,
  AppFile,
  ApprovalAdditionalField,
  CreditApplicationStepField,
  CreditApplicationTemplateType,
  InternalSignatureDocumentDefinition,
  isExternalFormApplicationType,
  isFormTemplateType,
} from '@dill/dill-shared';
import { AppImagePreviewModal } from '../../../general/AppImagePreviewModal/AppImagePreviewModal';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import { COLORS } from '../../../../utils/colors';
import { AppSuccessModal } from '../../../general/AppSuccessModal/AppSuccessModal';
import successChecksCircle from '../../../../assets/svg/success_checks_circle.svg';
import { useNavigate } from 'react-router-dom';
import NotFound from '../CreditApplicationComponents/NotFound';
import type { Field, FieldDetails, SignatureFields, Step } from './CONSTANTS';
import { collectIds, getTemplateFieldChoices } from './CONSTANTS';
import ApprovalMessageModal from '../CreditApplicationComponents/ApprovalMessageModal';
import InternalSignatureDocumentsModal from './InternalSignatureDocumentsModal';
import InternalFormSignersSection from './InternalFormSignersSection';
import InternalFormDecisionRecipientsSection, {
  DEFAULT_INTERNAL_FORM_DECISION_RECIPIENTS,
} from './InternalFormDecisionRecipientsSection';

const toCamelCase = (str: string) =>
  str
    .trim()
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .split(/\s+/)
    .map((word, i) =>
      i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('');

const EditCreditApplicationTemplate = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);
  const integrationName = getIntegrationName(currentSupplier?.integrationType);
  const { creditApplicationTemplate, loadingList } = useAppSelector(
    (globalState) => globalState.creditApplicationTemplates
  );
  const [currentCreditTemplateId, setCurrentCreditTemplateId] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  const [subsidiaryName, setSubsidiaryName] = useState<string>('');
  const [isAddEditFieldModalOpen, setIsAddEditFieldModalOpen] = useState(false);
  const [isAddStepModalOpen, setIsAddStepModalOpen] = useState(false);
  const [referenceFile, setReferenceFile] = useState<File | null>(null);
  const [bankReferenceFile, setBankReferenceFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);
  const [internalSignatureDocuments, setInternalSignatureDocuments] = useState<
    InternalSignatureDocumentDefinition[]
  >([]);
  const [isInternalSignatureDocumentsModalOpen, setIsInternalSignatureDocumentsModalOpen] =
    useState(false);
  const [selectedStep, setSelectedStep] = useState<string>('');
  const [applicationType, setApplicationType] =
    useState<CreditApplicationTemplateType>('CREDIT_APPLICATION');
  // Internal and external forms share the signers / decision recipients setup.
  const isFormTemplate = isFormTemplateType(applicationType);
  // Internal and external forms: what approving does to the Bill-To and Ship-To.
  const hasFormErpSteps = isFormTemplate || isExternalFormApplicationType(applicationType);
  const [bankReferenceFieldsRequired, setBankReferenceFieldsRequired] = useState<string[]>([]);
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [showPreviewPdf, setShowPreviewPdf] = useState(false);
  const [showPreviewImage, setShowPreviewImage] = useState(false);
  const [creditApplicationStepField, setCreditApplicationStepField] =
    useState<CreditApplicationStepField | null>(null);
  const [isUpdateSuccessModalOpen, setIsUpdateSuccessModalOpen] = useState(false);
  const [stepName, setStepName] = useState<string | null>(null);
  const [isApprovalMessageModalOpen, setIsApprovalMessageModalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [approvalSubjectLine, setApprovalSubjectLine] = useState('');
  const [customizedApprovalTemplate, setCustomizedApprovalTemplate] = useState(false);
  const [isRejectMessageModalOpen, setIsRejectMessageModalOpen] = useState(false);
  const [rejectCODMessage, setRejectMessage] = useState('');
  const [rejectSubjectLine, setRejectSubjectLine] = useState('');
  const [customizedRejectTemplate, setCustomizedRejectTemplate] = useState(false);
  const [approveCODReasons, setApproveCODReasons] = useState<string[]>([]);
  const [newReasonInput, setNewReasonInput] = useState('');
  const [internalFormSigners, setInternalFormSigners] = useState<InternalFormSignerStep[]>([]);
  const [internalFormDecisionRecipients, setInternalFormDecisionRecipients] =
    useState<InternalFormDecisionRecipients>(DEFAULT_INTERNAL_FORM_DECISION_RECIPIENTS);
  const [internalFormSkipErpUpdate, setInternalFormSkipErpUpdate] = useState(false);
  // Each account's ERP step on approval; '' leaves the choice to the approver.
  const [internalFormBillToAction, setInternalFormBillToAction] = useState<'UPDATE' | 'NONE' | ''>(
    ''
  );
  const [internalFormShipToAction, setInternalFormShipToAction] = useState<
    'UPDATE' | 'CREATE' | 'NONE' | ''
  >('');
  const [approvalAdditionalFields, setApprovalAdditionalFields] = useState<
    ApprovalAdditionalField[]
  >([]);
  const [newApprovalField, setNewApprovalField] = useState<{
    label: string;
    id: string;
    erpId: string;
    idManuallyEdited: boolean;
    type: 'text' | 'number' | 'currency' | 'dropdown';
    options: { name: string; erpId: string }[];
    required: boolean;
    defaultValue: string;
    allowCustomOption: boolean;
    optionsSource: 'CUSTOM' | 'SUPPLIER_BRANCHES';
  }>({
    label: '',
    id: '',
    erpId: '',
    idManuallyEdited: false,
    type: 'text',
    options: [],
    required: false,
    defaultValue: '',
    allowCustomOption: false,
    optionsSource: 'CUSTOM',
  });
  const [newOptionDraft, setNewOptionDraft] = useState({ name: '', erpId: '' });
  const [editingApprovalFieldIndex, setEditingApprovalFieldIndex] = useState<number | null>(null);
  const [isApproveCODMessageModalOpen, setIsApproveCODMessageModalOpen] = useState(false);
  const [approveCODMessage, setApproveCODMessage] = useState('');
  const [approveCODSubjectLine, setApproveCODSubjectLine] = useState('');
  const [customizedApproveCODTemplate, setCustomizedApproveCODTemplate] = useState(false);
  const [isInternallyApprovedMessageModalOpen, setIsInternallyApprovedMessageModalOpen] =
    useState(false);
  const [internallyApprovedMessage, setInternallyApprovedMessage] = useState('');
  const [internallyApprovedSubjectLine, setInternallyApprovedSubjectLine] = useState('');
  const [welcomeAttachments, setWelcomeAttachments] = useState<AppFile[]>([]);
  const [newWelcomeAttachmentFiles, setNewWelcomeAttachmentFiles] = useState<File[]>([]);
  const [customizedWelcomeTemplate, setCustomizedWelcomeTemplate] = useState(false);

  useEffect(() => {
    const pathlist = location.pathname.split('/');
    const creditTemplateId = pathlist[pathlist.length - 1];
    setCurrentCreditTemplateId(creditTemplateId);

    if (currentSupplier?.id) {
      dispatch(
        getCreditApplicationTemplate({
          templateId: creditTemplateId,
        })
      );
    }
  }, [location, currentSupplier?.id]);

  useEffect(() => {
    if (creditApplicationTemplate) {
      if (creditApplicationTemplate?.steps) {
        const normalizedSteps = creditApplicationTemplate.steps.map((step, stepIndex) => ({
          ...step,
          position: step.position ?? stepIndex,
          fields: step.fields.map((field, fieldIndex) => ({
            ...field,
            position: field.position ?? fieldIndex,
            groupedFields: (field.groupedFields ?? []).map((g) => ({
              ...g,
              stepType: 'GROUPED' as const,
            })),
            unGroupedFields: field.unGroupedFields ?? [],
          })),
        }));
        setSteps(normalizedSteps);
      }
      setInternalSignatureDocuments(creditApplicationTemplate?.internalSignatureDocuments ?? []);
      setApprovalMessage(creditApplicationTemplate?.approvalMessage ?? '');
      setApprovalSubjectLine(creditApplicationTemplate?.approvalSubjectLine ?? '');
      setCustomizedApprovalTemplate(creditApplicationTemplate?.customizedApprovalTemplate ?? false);
      setRejectMessage(creditApplicationTemplate?.rejectCODMessage ?? '');
      setRejectSubjectLine(creditApplicationTemplate?.rejectSubjectLine ?? '');
      setCustomizedRejectTemplate(creditApplicationTemplate?.customizedRejectTemplate ?? false);
      if (
        creditApplicationTemplate.approveCODReasons &&
        creditApplicationTemplate.approveCODReasons.length > 0
      ) {
        setApproveCODReasons(creditApplicationTemplate.approveCODReasons);
      }
      setWelcomeAttachments(creditApplicationTemplate?.welcomeAttachments ?? []);
      setCustomizedWelcomeTemplate(creditApplicationTemplate?.customizedWelcomeTemplate ?? false);
      setApproveCODMessage(creditApplicationTemplate?.approveCODMessage ?? '');
      setApproveCODSubjectLine(creditApplicationTemplate?.approveCODSubjectLine ?? '');
      setCustomizedApproveCODTemplate(
        creditApplicationTemplate?.customizedApproveCODTemplate ?? false
      );
      setInternallyApprovedMessage(creditApplicationTemplate?.internallyApprovedMessage ?? '');
      setInternallyApprovedSubjectLine(
        creditApplicationTemplate?.internallyApprovedSubjectLine ?? ''
      );
      setApplicationType(creditApplicationTemplate.applicationType);
      setTemplateName(creditApplicationTemplate.name ?? '');
      setSubsidiaryName(creditApplicationTemplate.subsidiaryName ?? '');
      if (creditApplicationTemplate.bankReferenceRequiredFields) {
        setBankReferenceFieldsRequired(creditApplicationTemplate.bankReferenceRequiredFields);
      }
      if (creditApplicationTemplate.approvalAdditionalFields) {
        setApprovalAdditionalFields(creditApplicationTemplate.approvalAdditionalFields);
      }
      setInternalFormSigners(creditApplicationTemplate.internalFormSigners ?? []);
      setInternalFormDecisionRecipients(
        creditApplicationTemplate.internalFormDecisionRecipients ??
          DEFAULT_INTERNAL_FORM_DECISION_RECIPIENTS
      );
      setInternalFormSkipErpUpdate(!!creditApplicationTemplate.internalFormSkipErpUpdate);
      setInternalFormBillToAction(creditApplicationTemplate.internalFormBillToAction ?? '');
      setInternalFormShipToAction(creditApplicationTemplate.internalFormShipToAction ?? '');
    }
  }, [creditApplicationTemplate]);

  const removeStep = (id: string) => {
    const updatedSteps = steps.filter((step) => step.id !== id);
    const reOrdered = updatedSteps.map((step, index) => ({ ...step, position: index }));
    setSteps(reOrdered);
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSteps.length) return;

    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    const updated = newSteps.map((s, i) => ({ ...s, position: i }));
    setSteps(updated);
  };

  const removeStepField = (stepId: string, fieldIndex: number) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) =>
        step.id === stepId
          ? {
              ...step,
              fields: step.fields
                .filter((_, i) => i !== fieldIndex)
                .map((field, i) => ({ ...field, position: i })),
            }
          : step
      )
    );
  };

  const moveStepField = (stepId: string, index: number, direction: 'up' | 'down') => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => {
        if (step.id !== stepId) return step;

        const fields = [...step.fields];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= fields.length) return step;

        [fields[index], fields[targetIndex]] = [fields[targetIndex], fields[index]];
        return {
          ...step,
          fields: fields.map((f, i) => ({ ...f, position: i })),
        };
      })
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    let currentFile: AppFile | null = null;
    if (referenceFile) {
      const uploadFileUrl = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(referenceFile),
        fileNameWithExtension: referenceFile.name,
        storagePath: `creditApplicationFiles/tradeRefenceFile/${
          currentSupplier?.id || ''
        }/${Date.now()}`,
      });
      const fileType = referenceFile.name.split('.').pop() || '';
      currentFile = {
        name: referenceFile.name,
        url: uploadFileUrl ?? '',
        fileType: fileType,
      };
    }
    let currentBankFile: AppFile | null = null;
    if (bankReferenceFile) {
      const uploadFileUrl = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(bankReferenceFile),
        fileNameWithExtension: bankReferenceFile.name,
        storagePath: `creditApplicationFiles/tbankReferenceFile/${
          currentSupplier?.id || ''
        }/${Date.now()}`,
      });
      const fileType = bankReferenceFile.name.split('.').pop() || '';
      currentBankFile = {
        name: bankReferenceFile.name,
        url: uploadFileUrl ?? '',
        fileType: fileType,
      };
    }
    const uploadedWelcomeAttachments: AppFile[] = [];
    for (const file of newWelcomeAttachmentFiles) {
      const uploadFileUrl = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(file),
        fileNameWithExtension: file.name,
        storagePath: `creditApplicationFiles/welcomeAttachments/${
          currentSupplier?.id || ''
        }/${Date.now()}`,
      });
      uploadedWelcomeAttachments.push({
        name: file.name,
        url: uploadFileUrl ?? '',
        fileType: file.name.split('.').pop() || '',
      });
    }
    const details = {
      approvalMessage,
      approvalSubjectLine,
      rejectCODMessage,
      rejectSubjectLine,
      approveCODMessage,
      approveCODSubjectLine,
      internallyApprovedMessage,
      internallyApprovedSubjectLine,
      customizedApprovalTemplate,
      customizedRejectTemplate,
      customizedApproveCODTemplate,
      approveCODReasons,
      approvalAdditionalFields,
      internalFormSigners: isFormTemplate ? internalFormSigners : [],
      internalFormDecisionRecipients: isFormTemplate ? internalFormDecisionRecipients : null,
      internalFormSkipErpUpdate: isFormTemplate && internalFormSkipErpUpdate,
      internalFormBillToAction: (hasFormErpSteps && internalFormBillToAction) || null,
      internalFormShipToAction: (hasFormErpSteps && internalFormShipToAction) || null,
      welcomeAttachments: [...welcomeAttachments, ...uploadedWelcomeAttachments],
      customizedWelcomeTemplate,
      ...(currentBankFile ? { bankReferenceFile: currentBankFile } : {}),
      ...(currentFile ? { tradeReferenceFile: currentFile } : {}),
    };

    const response = await dispatch(
      updateCreditApplicationTemplate({
        supplierId: currentSupplier?.id ?? '',
        templateId: currentCreditTemplateId,
        templateDetails: {
          steps,
          internalSignatureDocuments,
          applicationType,
          name: templateName || undefined,
          subsidiaryName: subsidiaryName.trim() || null,
          bankReferenceRequiredFields: bankReferenceFieldsRequired,
          ...details,
        },
      })
    );
    if (response.type === 'creditApplicationTemplates/updateCreditApplicationTemplate/fulfilled') {
      setSteps([]);
      setLoading(false);
      setIsUpdateSuccessModalOpen(true);
    } else {
      console.error('Failed to create credit application template:', response.payload);
      openDillToast({ message: 'Failed to create credit application template', type: 'ERROR' });
      setLoading(false);
    }
  };

  const handleAddNewField = (data: {
    columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
    stepType: 'GROUPED' | 'NOT_GROUPED';
    groupedFields: FieldDetails[];
    unGroupedFields: Field[];
    groupName?: string;
    isFieldArray: boolean;
    moreButtonName?: string;
    sendEmail?: boolean;
    bankReferenceRequiredFields?: string[];
    minimunNumberOfGroups?: number;
    signatureFields?: SignatureFields[];
    hasTradeReferenceUpload?: boolean;
  }) => {
    const updatedSteps = steps.map((step) => {
      if (step.id === selectedStep) {
        let newFields;

        if (creditApplicationStepField) {
          newFields = step.fields.map((f) =>
            f.position === creditApplicationStepField.position
              ? {
                  ...f,
                  columnType: data.columnType,
                  stepType: data.stepType,
                  groupedFields: data.groupedFields,
                  unGroupedFields: data.unGroupedFields,
                  groupName: data.groupName,
                  isFieldArray: data.isFieldArray,
                  ...(data.moreButtonName && { moreButtonName: data.moreButtonName }),
                  ...(data.sendEmail && { sendEmail: data.sendEmail }),
                  ...(data.minimunNumberOfGroups && {
                    minimunNumberOfGroups: data.minimunNumberOfGroups,
                  }),
                  ...(data.signatureFields?.length
                    ? { signatureFields: data.signatureFields }
                    : {}),
                  hasTradeReferenceUpload: data.hasTradeReferenceUpload ?? false,
                }
              : f
          );
        } else {
          newFields = [
            ...step.fields,
            {
              columnType: data.columnType,
              stepType: data.stepType,
              groupedFields: data.groupedFields,
              unGroupedFields: data.unGroupedFields,
              groupName: data.groupName,
              position: step.fields.length,
              isFieldArray: data.isFieldArray,
              ...(data.moreButtonName && { moreButtonName: data.moreButtonName }),
              ...(data.sendEmail && { sendEmail: data.sendEmail }),
              ...(data.minimunNumberOfGroups && {
                minimunNumberOfGroups: data.minimunNumberOfGroups,
              }),
              ...(data.signatureFields?.length ? { signatureFields: data.signatureFields } : {}),
              hasTradeReferenceUpload: data.hasTradeReferenceUpload ?? false,
            },
          ];
        }

        return {
          ...step,
          fields: newFields,
        };
      }
      return step;
    });

    if (data.bankReferenceRequiredFields) {
      setBankReferenceFieldsRequired(data.bankReferenceRequiredFields);
    }

    setSteps(updatedSteps);
    setCreditApplicationStepField(null);
  };

  const handleDownloadFile = async (fileUrl: string, fileName: string) => {
    downloadFile(fileUrl ?? '', fileName);
  };

  const handlePreviewFile = (url: string, fileType?: string) => {
    if (fileType && fileType !== 'pdf') {
      setAttachmentUrl(url);
      setShowPreviewImage(true);
    } else {
      setAttachmentUrl(url);
      setShowPreviewPdf(true);
    }
  };

  const editField = (field: CreditApplicationStepField, stepId: string) => {
    setSelectedStep(stepId);
    setCreditApplicationStepField(field);
    setIsAddEditFieldModalOpen(true);
  };

  const moveApprovalField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...approvalAdditionalFields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setApprovalAdditionalFields(newFields);
  };

  const startEditApprovalField = (index: number) => {
    const field = approvalAdditionalFields[index];
    setNewApprovalField({
      label: field.label,
      id: field.id,
      erpId: field.erpId ?? '',
      idManuallyEdited: true,
      type: field.type,
      options: (field.options ?? []).map((o) =>
        typeof o === 'object' && o !== null
          ? { name: o.name, erpId: o.erpId ?? '' }
          : { name: o, erpId: '' }
      ),
      required: field.required ?? false,
      defaultValue: field.defaultValue ?? '',
      allowCustomOption: field.allowCustomOption ?? false,
      optionsSource: field.optionsSource ?? 'CUSTOM',
    });
    setNewOptionDraft({ name: '', erpId: '' });
    setEditingApprovalFieldIndex(index);
  };

  const editStepName = (stepValue: string) => {
    setStepName(stepValue);
    setIsAddStepModalOpen(true);
  };

  const handleAddEditStep = (data: { name: string; id: string }) => {
    if (stepName) {
      const existingStep = steps.find((step) => step.name === stepName);
      if (existingStep) {
        setSteps(
          steps.map((step) => (step.id === existingStep.id ? { ...step, name: data.name } : step))
        );
      }
    } else {
      const filteredSteps = steps.filter((step) => step.id !== data.id);
      setSteps([
        ...filteredSteps,
        { name: data.name, id: data.id, position: filteredSteps.length, fields: [] },
      ]);
    }
    setStepName(null);
  };
  const stepFieldIds = useMemo(() => {
    return collectIds(steps);
  }, [steps]);

  return (
    <div>
      {!creditApplicationTemplate && !loadingList.includes('getCreditApplicationTemplate') && (
        <NotFound
          buttonText="Go Back"
          title="Credit/COD Application Template Not Found"
          path="/creditApplicationRequestTemplates/overview"
        />
      )}
      {creditApplicationTemplate && (
        <div className="flex flex-row h-full py-4">
          <AppSuccessModal
            open={isUpdateSuccessModalOpen}
            modalImage={successChecksCircle}
            title="Template Updated!"
            subTitle={`The ${applicationType
              .toLowerCase()
              .replace('_', ' ')} template has been successfully updated!`}
            buttonText={'View All Templates'}
            handleClose={() => {
              setIsUpdateSuccessModalOpen(false);
              navigate(
                buildSupplierPath(
                  currentSupplier?.id,
                  `/creditApplicationRequestTemplates/overview`
                )
              );
            }}
          />
          <AddStepModal
            open={isAddStepModalOpen}
            handleClose={() => setIsAddStepModalOpen(false)}
            onSubmit={handleAddEditStep}
            stepName={stepName}
          />
          <AppImagePreviewModal
            open={showPreviewImage}
            handleClose={() => {
              setShowPreviewImage(false);
            }}
            title="Credit App Preview"
            url={attachmentUrl}
          />
          <AppPdfPreviewModal
            open={showPreviewPdf}
            handleClose={() => {
              setShowPreviewPdf(false);
              setAttachmentUrl('');
            }}
            invoice={''}
            pdfType="CREDIT_APPLICATION"
            pdfList={[
              {
                name: 'Credit Application',
                url: attachmentUrl,
                date: new Date(),
              },
            ]}
            handleDownloadPdf={() => {}}
            isSigned={false}
            mode="Supplier"
          />
          <CreditApplicationFieldsModal
            open={isAddEditFieldModalOpen}
            handleClose={() => {
              setIsAddEditFieldModalOpen(false);
              setCreditApplicationStepField(null);
            }}
            onSubmit={handleAddNewField}
            selectedStep={selectedStep}
            creditApplicationStepField={creditApplicationStepField}
            selectedBankReferenceFieldsRequired={bankReferenceFieldsRequired}
            stepFieldIds={stepFieldIds}
          />
          <ApprovalMessageModal
            open={isApprovalMessageModalOpen}
            handleClose={() => setIsApprovalMessageModalOpen(false)}
            onSubmit={({ message, subjectLine }) => {
              setApprovalMessage(message);
              setApprovalSubjectLine(subjectLine);
            }}
            message={approvalMessage}
            subjectLine={approvalSubjectLine}
          />
          <ApprovalMessageModal
            open={isRejectMessageModalOpen}
            handleClose={() => setIsRejectMessageModalOpen(false)}
            onSubmit={({ message, subjectLine }) => {
              setRejectMessage(message);
              setRejectSubjectLine(subjectLine);
            }}
            message={rejectCODMessage}
            subjectLine={rejectSubjectLine}
            title="Add Reject Message"
          />
          <ApprovalMessageModal
            open={isApproveCODMessageModalOpen}
            handleClose={() => setIsApproveCODMessageModalOpen(false)}
            onSubmit={({ message, subjectLine }) => {
              setApproveCODMessage(message);
              setApproveCODSubjectLine(subjectLine);
            }}
            message={approveCODMessage}
            subjectLine={approveCODSubjectLine}
            title="Add Approve COD Message"
          />
          <ApprovalMessageModal
            open={isInternallyApprovedMessageModalOpen}
            handleClose={() => setIsInternallyApprovedMessageModalOpen(false)}
            onSubmit={({ message, subjectLine }) => {
              setInternallyApprovedMessage(message);
              setInternallyApprovedSubjectLine(subjectLine);
            }}
            message={internallyApprovedMessage}
            subjectLine={internallyApprovedSubjectLine}
            title="Add Internal Approve Message"
          />
          <div className={twMerge('flex flex-col w-1/3 mx-4 h-full ')}>
            <div className="h-[calc(100vh-90px)] relative overflow-y-scroll flex flex-col items-center pb-3 pt-0">
              <div className="flex flex-col bg-white px-4 pb-3 mb-3 w-full">
                <div className="flex flex-col w-full sticky top-0 bg-white z-10 mb-3 pt-2">
                  <div className="mb-2 w-full">
                    <div className="text-xs font-semibold mb-1">Title</div>
                    <input
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="e.g. CREDIT APPLICATION AND AGREEMENT"
                      className="h-9 px-3 text-sm rounded-lg border border-gray-300 w-full outline-none focus:border-blue-400 mb-2"
                    />
                  </div>
                  <div className="mb-2 w-full">
                    <div className="text-xs font-semibold mb-1">Subsidiary Name</div>
                    <input
                      type="text"
                      value={subsidiaryName}
                      onChange={(e) => setSubsidiaryName(e.target.value)}
                      className="h-9 px-3 text-sm rounded-lg border border-gray-300 w-full outline-none focus:border-blue-400"
                    />
                    <p className="text-xs GREY_500-CLR mt-1">
                      {`Shown as "Application Type - Subsidiary Name" wherever this template is picked.`}
                    </p>
                  </div>
                  <div className="mb-2 w-full">
                    <AppDropDown
                      isRequired
                      label="Application Type"
                      value={applicationType}
                      items={[
                        { label: 'Credit Application', value: 'CREDIT_APPLICATION' },
                        { label: 'COD Application', value: 'CASH_APPLICATION' },
                        { label: 'Internal Form', value: 'INTERNAL_FORM' },
                        { label: 'External Form', value: 'EXTERNAL_FORM' },
                      ]}
                      onSelectChange={(item) => {
                        if (!item?.value) return;
                        setApplicationType(item.value as CreditApplicationTemplateType);
                      }}
                    />
                  </div>
                  {/* Trade/bank reference files aren't used by internal / external forms */}
                  {!isFormTemplate && (
                    <>
                      <div className="mb-2 w-full gap-4">
                        <div className="w-full flex">
                          <UploadFileInputs
                            label="Trade Reference File"
                            onSelectFile={(file) => setReferenceFile(file.file)}
                            selectedFile={referenceFile}
                          />
                          {creditApplicationTemplate &&
                            creditApplicationTemplate.tradeReferenceFile &&
                            creditApplicationTemplate.tradeReferenceFile.url && (
                              <div className="w-full ml-1">
                                <div className="text-xs font-semibold mb-1">
                                  Current Trade Reference File:
                                </div>
                                <div className="flex gap-2 items-center">
                                  <div className="flex items-center gap-2">
                                    <DillButton
                                      variant="icon-default"
                                      height="25px"
                                      width="25px"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleDownloadFile(
                                          creditApplicationTemplate?.tradeReferenceFile?.url ?? '',
                                          creditApplicationTemplate?.tradeReferenceFile?.name ?? ''
                                        );
                                      }}
                                      icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                                    />
                                    {(creditApplicationTemplate?.tradeReferenceFile?.fileType ===
                                      'pdf' ||
                                      creditApplicationTemplate?.tradeReferenceFile?.fileType ===
                                        'jpg' ||
                                      creditApplicationTemplate?.tradeReferenceFile?.fileType ===
                                        'jpeg' ||
                                      creditApplicationTemplate?.tradeReferenceFile?.fileType ===
                                        'png') && (
                                      <DillButton
                                        variant="icon-default"
                                        height="25px"
                                        width="25px"
                                        icon={<Eye color={COLORS.GREY_500} size={14} />}
                                        onClick={() => {
                                          handlePreviewFile(
                                            creditApplicationTemplate?.tradeReferenceFile?.url ??
                                              '',
                                            creditApplicationTemplate?.tradeReferenceFile?.fileType
                                          );
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div className="text-xs">
                                    {creditApplicationTemplate?.tradeReferenceFile?.name ?? ''}
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                      <div className="mb-2 w-full gap-4">
                        <div className="w-full flex">
                          <UploadFileInputs
                            label="Bank Reference File"
                            onSelectFile={(file) => setBankReferenceFile(file.file)}
                            selectedFile={bankReferenceFile}
                          />
                          {creditApplicationTemplate &&
                            creditApplicationTemplate.bankReferenceFile &&
                            creditApplicationTemplate.bankReferenceFile.url && (
                              <div className="w-full ml-1">
                                <div className="text-xs font-semibold mb-1">
                                  Current Bank Reference File:
                                </div>
                                <div className="flex gap-2 items-center">
                                  <div className="flex items-center gap-2">
                                    <DillButton
                                      variant="icon-default"
                                      height="25px"
                                      width="25px"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleDownloadFile(
                                          creditApplicationTemplate?.bankReferenceFile?.url ?? '',
                                          creditApplicationTemplate?.bankReferenceFile?.name ?? ''
                                        );
                                      }}
                                      icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                                    />
                                    {(creditApplicationTemplate?.bankReferenceFile?.fileType ===
                                      'pdf' ||
                                      creditApplicationTemplate?.bankReferenceFile?.fileType ===
                                        'jpg' ||
                                      creditApplicationTemplate?.bankReferenceFile?.fileType ===
                                        'jpeg' ||
                                      creditApplicationTemplate?.bankReferenceFile?.fileType ===
                                        'png') && (
                                      <DillButton
                                        variant="icon-default"
                                        height="25px"
                                        width="25px"
                                        icon={<Eye color={COLORS.GREY_500} size={14} />}
                                        onClick={() => {
                                          handlePreviewFile(
                                            creditApplicationTemplate?.bankReferenceFile?.url ?? '',
                                            creditApplicationTemplate?.bankReferenceFile?.fileType
                                          );
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div className="text-xs">
                                    {creditApplicationTemplate?.bankReferenceFile?.name ?? ''}
                                  </div>
                                </div>
                              </div>
                            )}
                        </div>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between mt-1 ">
                    <p className="text-lg font-bold">Steps</p>
                    <DillButton
                      label="Add Step"
                      height="30px"
                      onClick={() => setIsAddStepModalOpen(true)}
                    />
                  </div>
                  {steps
                    .sort((a, b) => a.position - b.position)
                    .map((step, index) => (
                      <div key={step.id} className="border rounded p-2 mt-2">
                        <div className="flex justify-between items-center">
                          <p className="text-sm font-bold">{step.name}</p>
                          <div className="flex gap-2">
                            {index > 0 && (
                              <DillButton
                                variant="icon-default"
                                height="30px"
                                width="30px"
                                onClick={() => moveStep(index, 'up')}
                                icon={<ArrowUp size={12} />}
                              />
                            )}
                            {index < steps.length - 1 && (
                              <DillButton
                                variant="icon-default"
                                height="30px"
                                width="30px"
                                onClick={() => moveStep(index, 'down')}
                                icon={<ArrowDown size={12} />}
                              />
                            )}
                            <DillButton
                              label="Add Field"
                              height="30px"
                              width="70px"
                              onClick={() => {
                                setSelectedStep(step.id);
                                setIsAddEditFieldModalOpen(true);
                              }}
                            />
                            <DillButton
                              variant="icon-default"
                              height="30px"
                              width="30px"
                              icon={<Edit3 size={12} />}
                              onClick={() => editStepName(step.name)}
                            />
                            <DillButton
                              variant="icon-default"
                              height="30px"
                              width="30px"
                              icon={<X size={12} />}
                              onClick={() => removeStep(step.id)}
                            />
                          </div>
                        </div>
                        <div className="mt-2">
                          {step.fields
                            .sort((a, b) => a.position - b.position)
                            .map((field, fieldIndex) => (
                              <div
                                key={`${step.id}-${fieldIndex}`}
                                className="border rounded p-2 mt-2">
                                <div className="flex justify-between items-center">
                                  <p className="text-sm font-semibold">
                                    {field.groupName || 'Ungrouped'}
                                  </p>
                                  <div className="flex gap-2">
                                    {fieldIndex > 0 && (
                                      <DillButton
                                        variant="icon-default"
                                        height="25px"
                                        width="25px"
                                        icon={<ArrowUp size={12} />}
                                        onClick={() => moveStepField(step.id, fieldIndex, 'up')}
                                      />
                                    )}
                                    {fieldIndex < step.fields.length - 1 && (
                                      <DillButton
                                        variant="icon-default"
                                        height="25px"
                                        width="25px"
                                        icon={<ArrowDown size={12} />}
                                        onClick={() => moveStepField(step.id, fieldIndex, 'down')}
                                      />
                                    )}
                                    <DillButton
                                      variant="icon-default"
                                      height="25px"
                                      width="25px"
                                      icon={<Edit3 size={12} />}
                                      onClick={() => editField(field, step.id)}
                                    />
                                    <DillButton
                                      variant="icon-default"
                                      height="25px"
                                      width="25px"
                                      icon={<X size={12} />}
                                      onClick={() => removeStepField(step.id, fieldIndex)}
                                    />
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1 mt-2">
                                  {field.unGroupedFields.map((f, fIndex) => (
                                    <div key={`${f.id}-${fIndex}`} className="text-sm">
                                      {isHTML(f.name) ? (
                                        <span
                                          dangerouslySetInnerHTML={{
                                            __html: toInlineHTML(f.name),
                                          }}
                                        />
                                      ) : (
                                        f.name
                                      )}{' '}
                                      ({f.fromField})
                                    </div>
                                  ))}
                                  {field.groupedFields.map((group, groupIndex) => (
                                    <div key={`${group.id}-${groupIndex}`} className="text-sm">
                                      {group.fields.map((gField, gIndex) => (
                                        <span key={`${gField.id}-${gIndex}`}>
                                          {isHTML(gField.name) ? (
                                            <span
                                              dangerouslySetInnerHTML={{
                                                __html: toInlineHTML(gField.name),
                                              }}
                                            />
                                          ) : (
                                            gField.name
                                          )}{' '}
                                          ({gField.fromField}){' '}
                                        </span>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  {isFormTemplate && (
                    <>
                      <InternalFormSignersSection
                        signers={internalFormSigners}
                        onChange={setInternalFormSigners}
                      />
                      <InternalFormDecisionRecipientsSection
                        recipients={internalFormDecisionRecipients}
                        onChange={setInternalFormDecisionRecipients}
                      />
                      <label className="border rounded p-2 mt-2 flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={internalFormSkipErpUpdate}
                          onChange={(e) => setInternalFormSkipErpUpdate(e.target.checked)}
                        />
                        <span className="text-xs text-gray-700">
                          Never update {integrationName} when approving (approvals are always
                          &quot;Don&apos;t update {integrationName}&quot;)
                        </span>
                      </label>
                    </>
                  )}
                  {hasFormErpSteps && !internalFormSkipErpUpdate && (
                    <div className="border rounded p-2 mt-2 flex flex-col gap-2">
                      <p className="text-sm font-semibold">
                        What approving does in {integrationName}
                      </p>
                      <DillDropdown
                        containerClassName="h-[40px] p-1"
                        label="Bill-To"
                        value={internalFormBillToAction || 'CHOOSE'}
                        items={[
                          { value: 'CHOOSE', label: 'The approver chooses' },
                          {
                            value: 'UPDATE',
                            label: 'Update it with the form’s Bill-To details',
                          },
                          {
                            value: 'NONE',
                            label: 'Don’t change it (only the Ship-To’s parent)',
                          },
                        ]}
                        onSelectChange={(item) =>
                          setInternalFormBillToAction(
                            item.value === 'CHOOSE' ? '' : (item.value as 'UPDATE' | 'NONE')
                          )
                        }
                      />
                      <DillDropdown
                        containerClassName="h-[40px] p-1"
                        label="Ship-To"
                        value={internalFormShipToAction || 'CHOOSE'}
                        items={[
                          { value: 'CHOOSE', label: 'The approver chooses' },
                          { value: 'UPDATE', label: 'Update an existing Ship-To' },
                          { value: 'CREATE', label: 'Create a new Ship-To under the Bill-To' },
                          { value: 'NONE', label: 'Don’t change it' },
                        ]}
                        onSelectChange={(item) =>
                          setInternalFormShipToAction(
                            item.value === 'CHOOSE'
                              ? ''
                              : (item.value as 'UPDATE' | 'CREATE' | 'NONE')
                          )
                        }
                      />
                      <p className="text-xs ml-1 text-left GREY_500-CLR">
                        Approving always asks for the Bill-To number, and for the Ship-To number
                        when an existing Ship-To is updated. Reactivate: Update / Update. New
                        Ship-To and Job Information: Don’t change / Create.
                      </p>
                    </div>
                  )}
                  {/* Approval/rejection messages, welcome emails and signature documents don't apply to internal / external forms */}
                  {!isFormTemplate && (
                    <>
                      <div className="border rounded p-2 mt-2">
                        {(approvalSubjectLine || approvalMessage) && (
                          <label className="flex items-center gap-2 cursor-pointer mb-2">
                            <input
                              type="checkbox"
                              checked={customizedApprovalTemplate}
                              onChange={(e) => setCustomizedApprovalTemplate(e.target.checked)}
                              className="w-4 h-4"
                            />
                            <span className="text-xs font-semibold">
                              Override Approval Template
                            </span>
                          </label>
                        )}
                        {(approvalSubjectLine || approvalMessage) && (
                          <div className="mb-2">
                            {approvalSubjectLine && (
                              <p className="text-xs font-semibold text-gray-600 mb-1">
                                Subject: {approvalSubjectLine}
                              </p>
                            )}
                            {approvalMessage && (
                              <div>
                                {isHTML(approvalMessage) ? (
                                  <div dangerouslySetInnerHTML={{ __html: approvalMessage }} />
                                ) : (
                                  <p>{approvalMessage}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <DillButton
                            label={
                              approvalMessage ? 'Edit Approval Message' : 'Add Approval Message'
                            }
                            height="30px"
                            onClick={() => setIsApprovalMessageModalOpen(true)}
                          />
                          {approvalMessage && (
                            <DillButton
                              variant="icon-default"
                              height="30px"
                              width="30px"
                              icon={<X size={12} />}
                              onClick={() => {
                                setApprovalMessage('');
                                setApprovalSubjectLine('');
                                setCustomizedApprovalTemplate(false);
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="border rounded p-2 mt-2">
                        {(rejectSubjectLine || rejectCODMessage) && (
                          <label className="flex items-center gap-2 cursor-pointer mb-2">
                            <input
                              type="checkbox"
                              checked={customizedRejectTemplate}
                              onChange={(e) => setCustomizedRejectTemplate(e.target.checked)}
                              className="w-4 h-4"
                            />
                            <span className="text-xs font-semibold">Override Decline Template</span>
                          </label>
                        )}
                        {(rejectSubjectLine || rejectCODMessage) && (
                          <div className="mb-2">
                            {rejectSubjectLine && (
                              <p className="text-xs font-semibold text-gray-600 mb-1">
                                Subject: {rejectSubjectLine}
                              </p>
                            )}
                            {rejectCODMessage && (
                              <div>
                                {isHTML(rejectCODMessage) ? (
                                  <div dangerouslySetInnerHTML={{ __html: rejectCODMessage }} />
                                ) : (
                                  <p>{rejectCODMessage}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <DillButton
                            label={rejectCODMessage ? 'Edit Reject Message' : 'Add Reject Message'}
                            height="30px"
                            onClick={() => setIsRejectMessageModalOpen(true)}
                          />
                          {rejectCODMessage && (
                            <DillButton
                              variant="icon-default"
                              height="30px"
                              width="30px"
                              icon={<X size={12} />}
                              onClick={() => {
                                setRejectMessage('');
                                setRejectSubjectLine('');
                                setCustomizedRejectTemplate(false);
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="border rounded p-2 mt-2">
                        {(approveCODSubjectLine || approveCODMessage) && (
                          <label className="flex items-center gap-2 cursor-pointer mb-2">
                            <input
                              type="checkbox"
                              checked={customizedApproveCODTemplate}
                              onChange={(e) => setCustomizedApproveCODTemplate(e.target.checked)}
                              className="w-4 h-4"
                            />
                            <span className="text-xs font-semibold">
                              Override Approve COD Template
                            </span>
                          </label>
                        )}
                        {(approveCODSubjectLine || approveCODMessage) && (
                          <div className="mb-2">
                            {approveCODSubjectLine && (
                              <p className="text-xs font-semibold text-gray-600 mb-1">
                                Subject: {approveCODSubjectLine}
                              </p>
                            )}
                            {approveCODMessage && (
                              <div>
                                {isHTML(approveCODMessage) ? (
                                  <div dangerouslySetInnerHTML={{ __html: approveCODMessage }} />
                                ) : (
                                  <p>{approveCODMessage}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <DillButton
                            label={
                              approveCODMessage
                                ? 'Edit Approve COD Message'
                                : 'Add Approve COD Message'
                            }
                            height="30px"
                            onClick={() => setIsApproveCODMessageModalOpen(true)}
                          />
                          {approveCODMessage && (
                            <DillButton
                              variant="icon-default"
                              height="30px"
                              width="30px"
                              icon={<X size={12} />}
                              onClick={() => {
                                setApproveCODMessage('');
                                setApproveCODSubjectLine('');
                                setCustomizedApproveCODTemplate(false);
                              }}
                            />
                          )}
                        </div>
                        <div className="mt-3">
                          <p className="text-xs font-semibold mb-1">Rejection Reasons</p>
                          <div className="flex flex-col gap-1 mb-2">
                            {approveCODReasons.map((r, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <span className="text-xs flex-1 text-gray-700">{r}</span>
                                <DillButton
                                  variant="icon-default"
                                  height="18px"
                                  width="18px"
                                  icon={<X size={10} />}
                                  onClick={() =>
                                    setApproveCODReasons(
                                      approveCODReasons.filter((_, idx) => idx !== i)
                                    )
                                  }
                                />
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              value={newReasonInput}
                              onChange={(e) => setNewReasonInput(e.target.value)}
                              placeholder="Add a reason"
                              className="text-xs border rounded px-2 py-1 flex-1 outline-none focus:border-blue-400"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newReasonInput.trim()) {
                                  setApproveCODReasons([
                                    ...approveCODReasons,
                                    newReasonInput.trim(),
                                  ]);
                                  setNewReasonInput('');
                                }
                              }}
                            />
                            <DillButton
                              label="Add"
                              height="26px"
                              onClick={() => {
                                if (newReasonInput.trim()) {
                                  setApproveCODReasons([
                                    ...approveCODReasons,
                                    newReasonInput.trim(),
                                  ]);
                                  setNewReasonInput('');
                                }
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {/* Asked for on approval — credit applications and internal / external forms alike */}
                  <div className="border rounded p-2 mt-2">
                    <p className="text-xs font-semibold mb-2">Approval Additional Fields</p>
                    <div className="flex flex-col gap-1 mb-2">
                      {approvalAdditionalFields.map((field, i) => (
                        <div
                          key={field.id}
                          className="flex items-center gap-2 bg-gray-50 rounded px-2 py-1">
                          <div className="flex-1 text-xs text-gray-700">
                            <span className="font-medium">{field.label}</span>
                            <span className="text-gray-400 ml-1">({field.type})</span>
                            {field.options && field.options.length > 0 && (
                              <span className="text-gray-400 ml-1">
                                {'— '}
                                {field.options
                                  .map((o) => (typeof o === 'object' && o !== null ? o.name : o))
                                  .join(', ')}
                              </span>
                            )}
                            {field.optionsSource === 'SUPPLIER_BRANCHES' && (
                              <span className="text-gray-400 ml-1">— supplier branches</span>
                            )}
                            {field.required && <span className="text-red-400 ml-1">*required</span>}
                          </div>
                          <div className="flex gap-1">
                            {i > 0 && (
                              <DillButton
                                variant="icon-default"
                                height="18px"
                                width="18px"
                                icon={<ArrowUp size={10} />}
                                onClick={() => moveApprovalField(i, 'up')}
                              />
                            )}
                            {i < approvalAdditionalFields.length - 1 && (
                              <DillButton
                                variant="icon-default"
                                height="18px"
                                width="18px"
                                icon={<ArrowDown size={10} />}
                                onClick={() => moveApprovalField(i, 'down')}
                              />
                            )}
                            <DillButton
                              variant="icon-default"
                              height="18px"
                              width="18px"
                              icon={<Edit3 size={10} />}
                              onClick={() => startEditApprovalField(i)}
                            />
                            <DillButton
                              variant="icon-default"
                              height="18px"
                              width="18px"
                              icon={<X size={10} />}
                              onClick={() => {
                                setApprovalAdditionalFields(
                                  approvalAdditionalFields.filter((_, idx) => idx !== i)
                                );
                                if (editingApprovalFieldIndex === i) {
                                  setEditingApprovalFieldIndex(null);
                                  setNewApprovalField({
                                    label: '',
                                    id: '',
                                    erpId: '',
                                    idManuallyEdited: false,
                                    type: 'text',
                                    options: [],
                                    required: false,
                                    defaultValue: '',
                                    allowCustomOption: false,
                                    optionsSource: 'CUSTOM',
                                  });
                                }
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-col gap-2 border-t pt-2">
                      {editingApprovalFieldIndex !== null && (
                        <p className="text-xs text-blue-500 font-medium">
                          Editing: {approvalAdditionalFields[editingApprovalFieldIndex]?.label}
                        </p>
                      )}
                      <input
                        type="text"
                        value={newApprovalField.label}
                        onChange={(e) => {
                          const label = e.target.value;
                          setNewApprovalField({
                            ...newApprovalField,
                            label,
                            id: newApprovalField.idManuallyEdited
                              ? newApprovalField.id
                              : toCamelCase(label),
                          });
                        }}
                        placeholder="Field label (e.g. Credit Status)"
                        className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400"
                      />
                      <input
                        type="text"
                        value={newApprovalField.id}
                        onChange={(e) =>
                          setNewApprovalField({
                            ...newApprovalField,
                            id: e.target.value,
                            idManuallyEdited: true,
                          })
                        }
                        placeholder="Field ID (auto-generated)"
                        className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400 text-gray-500"
                      />
                      <input
                        type="text"
                        value={newApprovalField.erpId}
                        onChange={(e) =>
                          setNewApprovalField({ ...newApprovalField, erpId: e.target.value })
                        }
                        placeholder="ERP ID (optional)"
                        className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400 text-gray-500"
                      />
                      <DillDropdown
                        value={newApprovalField.type}
                        isSearchable={false}
                        items={[
                          { value: 'text', label: 'Text' },
                          { value: 'number', label: 'Number' },
                          { value: 'currency', label: 'Currency ($)' },
                          { value: 'dropdown', label: 'Dropdown' },
                        ]}
                        onSelectChange={(item) =>
                          setNewApprovalField({
                            ...newApprovalField,
                            type: item.value as 'text' | 'number' | 'currency' | 'dropdown',
                          })
                        }
                      />
                      {newApprovalField.type === 'dropdown' && (
                        <>
                          <DillDropdown
                            label="Options from"
                            value={newApprovalField.optionsSource}
                            isSearchable={false}
                            items={[
                              { value: 'CUSTOM', label: 'Custom options' },
                              { value: 'SUPPLIER_BRANCHES', label: 'Supplier branches' },
                            ]}
                            onSelectChange={(item) =>
                              setNewApprovalField({
                                ...newApprovalField,
                                optionsSource: item.value as 'CUSTOM' | 'SUPPLIER_BRANCHES',
                              })
                            }
                          />
                          {newApprovalField.optionsSource === 'SUPPLIER_BRANCHES' ? (
                            <p className="text-xs text-gray-500">
                              The approver picks one of the supplier&apos;s branches; its ERP ID is
                              saved with it.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {newApprovalField.options.map((opt, optIdx) => (
                                <div key={optIdx} className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={opt.name}
                                    onChange={(e) => {
                                      const updated = [...newApprovalField.options];
                                      updated[optIdx] = {
                                        ...updated[optIdx],
                                        name: e.target.value,
                                      };
                                      setNewApprovalField({
                                        ...newApprovalField,
                                        options: updated,
                                      });
                                    }}
                                    placeholder="Option name"
                                    className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400 flex-1"
                                  />
                                  <input
                                    type="text"
                                    value={opt.erpId}
                                    onChange={(e) => {
                                      const updated = [...newApprovalField.options];
                                      updated[optIdx] = {
                                        ...updated[optIdx],
                                        erpId: e.target.value,
                                      };
                                      setNewApprovalField({
                                        ...newApprovalField,
                                        options: updated,
                                      });
                                    }}
                                    placeholder="ERP ID"
                                    className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400 w-24 text-gray-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setNewApprovalField({
                                        ...newApprovalField,
                                        options: newApprovalField.options.filter(
                                          (_, i) => i !== optIdx
                                        ),
                                      })
                                    }
                                    className="text-gray-400 hover:text-red-400 text-xs px-1">
                                    ✕
                                  </button>
                                </div>
                              ))}
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={newOptionDraft.name}
                                  onChange={(e) =>
                                    setNewOptionDraft({ ...newOptionDraft, name: e.target.value })
                                  }
                                  placeholder="Option name"
                                  className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400 flex-1"
                                />
                                <input
                                  type="text"
                                  value={newOptionDraft.erpId}
                                  onChange={(e) =>
                                    setNewOptionDraft({
                                      ...newOptionDraft,
                                      erpId: e.target.value,
                                    })
                                  }
                                  placeholder="ERP ID"
                                  className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400 w-24 text-gray-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!newOptionDraft.name.trim()) return;
                                    setNewApprovalField({
                                      ...newApprovalField,
                                      options: [...newApprovalField.options, { ...newOptionDraft }],
                                    });
                                    setNewOptionDraft({ name: '', erpId: '' });
                                  }}
                                  className="text-xs text-blue-500 hover:text-blue-700 px-1 whitespace-nowrap">
                                  + Add
                                </button>
                              </div>
                            </div>
                          )}
                          <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newApprovalField.allowCustomOption}
                              onChange={(e) =>
                                setNewApprovalField({
                                  ...newApprovalField,
                                  allowCustomOption: e.target.checked,
                                })
                              }
                              className="w-3 h-3"
                            />
                            Allow custom "Other" option
                          </label>
                        </>
                      )}
                      <input
                        type="text"
                        value={newApprovalField.defaultValue}
                        onChange={(e) =>
                          setNewApprovalField({
                            ...newApprovalField,
                            defaultValue: e.target.value,
                          })
                        }
                        placeholder="Default value (optional)"
                        className="text-xs border rounded px-2 py-1 outline-none focus:border-blue-400"
                      />
                      <label className="flex items-center gap-2 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newApprovalField.required}
                          onChange={(e) =>
                            setNewApprovalField({
                              ...newApprovalField,
                              required: e.target.checked,
                            })
                          }
                          className="w-3 h-3"
                        />
                        Required field
                      </label>
                      <div className="flex gap-2">
                        {editingApprovalFieldIndex !== null && (
                          <DillButton
                            label="Cancel"
                            height="26px"
                            variant="secondary"
                            onClick={() => {
                              setEditingApprovalFieldIndex(null);
                              setNewApprovalField({
                                label: '',
                                id: '',
                                erpId: '',
                                idManuallyEdited: false,
                                type: 'text',
                                options: [],
                                required: false,
                                defaultValue: '',
                                allowCustomOption: false,
                                optionsSource: 'CUSTOM',
                              });
                            }}
                          />
                        )}
                        <DillButton
                          label={editingApprovalFieldIndex !== null ? 'Update Field' : 'Add Field'}
                          height="26px"
                          onClick={() => {
                            if (!newApprovalField.label.trim()) return;
                            const isBranchesDropdown =
                              newApprovalField.type === 'dropdown' &&
                              newApprovalField.optionsSource === 'SUPPLIER_BRANCHES';
                            const options =
                              newApprovalField.type === 'dropdown' && !isBranchesDropdown
                                ? newApprovalField.options
                                    .filter((o) => o.name.trim())
                                    .map((o) =>
                                      o.erpId.trim()
                                        ? { name: o.name.trim(), erpId: o.erpId.trim() }
                                        : o.name.trim()
                                    )
                                : [];
                            const fieldData = {
                              id: newApprovalField.id.trim() || toCamelCase(newApprovalField.label),
                              label: newApprovalField.label.trim(),
                              erpId: newApprovalField.erpId.trim() || undefined,
                              type: newApprovalField.type,
                              options,
                              required: newApprovalField.required,
                              defaultValue: newApprovalField.defaultValue.trim() || undefined,
                              allowCustomOption: newApprovalField.allowCustomOption,
                              ...(isBranchesDropdown
                                ? { optionsSource: 'SUPPLIER_BRANCHES' as const }
                                : {}),
                            };
                            if (editingApprovalFieldIndex !== null) {
                              const updated = [...approvalAdditionalFields];
                              updated[editingApprovalFieldIndex] = fieldData;
                              setApprovalAdditionalFields(updated);
                              setEditingApprovalFieldIndex(null);
                            } else {
                              setApprovalAdditionalFields([...approvalAdditionalFields, fieldData]);
                            }
                            setNewApprovalField({
                              label: '',
                              id: '',
                              erpId: '',
                              idManuallyEdited: false,
                              type: 'text',
                              options: [],
                              required: false,
                              defaultValue: '',
                              allowCustomOption: false,
                              optionsSource: 'CUSTOM',
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  {!isFormTemplate && (
                    <>
                      <div className="border rounded p-2 mt-2">
                        {(internallyApprovedSubjectLine || internallyApprovedMessage) && (
                          <div className="mb-2">
                            {internallyApprovedSubjectLine && (
                              <p className="text-xs font-semibold text-gray-600 mb-1">
                                Subject: {internallyApprovedSubjectLine}
                              </p>
                            )}
                            {internallyApprovedMessage && (
                              <div>
                                {isHTML(internallyApprovedMessage) ? (
                                  <div
                                    dangerouslySetInnerHTML={{ __html: internallyApprovedMessage }}
                                  />
                                ) : (
                                  <p>{internallyApprovedMessage}</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <DillButton
                            label={
                              internallyApprovedMessage
                                ? 'Edit Internal Approve Message'
                                : 'Add Internal Approve Message'
                            }
                            height="30px"
                            onClick={() => setIsInternallyApprovedMessageModalOpen(true)}
                          />
                          {internallyApprovedMessage && (
                            <DillButton
                              variant="icon-default"
                              height="30px"
                              width="30px"
                              icon={<X size={12} />}
                              onClick={() => {
                                setInternallyApprovedMessage('');
                                setInternallyApprovedSubjectLine('');
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="border rounded p-2 mt-2">
                        <div className="text-xs font-semibold mb-2">Welcome Attachments</div>
                        {welcomeAttachments.map((file, index) => (
                          <div key={index} className="flex items-center gap-2 mb-1">
                            <div className="text-xs truncate max-w-xs">{file.name}</div>
                            <DillButton
                              variant="icon-default"
                              height="20px"
                              width="20px"
                              icon={<X size={10} />}
                              onClick={() =>
                                setWelcomeAttachments(
                                  welcomeAttachments.filter((_, i) => i !== index)
                                )
                              }
                            />
                          </div>
                        ))}
                        {newWelcomeAttachmentFiles.map((file, index) => (
                          <div key={`new-${index}`} className="flex items-center gap-2 mb-1">
                            <div className="text-xs truncate max-w-xs text-blue-500">
                              {file.name}
                            </div>
                            <DillButton
                              variant="icon-default"
                              height="20px"
                              width="20px"
                              icon={<X size={10} />}
                              onClick={() =>
                                setNewWelcomeAttachmentFiles(
                                  newWelcomeAttachmentFiles.filter((_, i) => i !== index)
                                )
                              }
                            />
                          </div>
                        ))}
                        <UploadFileInputs
                          label="Add Attachment"
                          onSelectFile={(data) =>
                            setNewWelcomeAttachmentFiles([...newWelcomeAttachmentFiles, data.file])
                          }
                        />
                      </div>
                      <div className="border rounded p-2 mt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={customizedWelcomeTemplate}
                            onChange={(e) => setCustomizedWelcomeTemplate(e.target.checked)}
                            className="w-4 h-4"
                          />
                          <span className="text-xs font-semibold">Customized Welcome Template</span>
                        </label>
                      </div>
                      <div className="border rounded p-2 mt-2 flex flex-col gap-2">
                        <span className="text-xs font-semibold">Internal Signature Documents</span>
                        {internalSignatureDocuments.length > 0 ? (
                          <div className="flex flex-col gap-2">
                            {internalSignatureDocuments.map((form) => (
                              <div
                                key={form.id}
                                className="w-full flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2">
                                <span className="text-sm text-gray-800 truncate">
                                  {form.name || 'Untitled Internal Signature Document'}
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                  <DillButton
                                    variant="icon-default"
                                    height="30px"
                                    width="30px"
                                    icon={<Edit3 size={12} />}
                                    onClick={() => setIsInternalSignatureDocumentsModalOpen(true)}
                                  />
                                  <DillButton
                                    variant="icon-default"
                                    height="30px"
                                    width="30px"
                                    icon={<X size={12} />}
                                    onClick={() =>
                                      setInternalSignatureDocuments(
                                        internalSignatureDocuments.filter((f) => f.id !== form.id)
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400">
                            No internal signature documents added
                          </p>
                        )}
                        <DillButton
                          label={
                            internalSignatureDocuments.length > 0
                              ? 'Manage Internal Signature Documents'
                              : 'Add Internal Signature Document'
                          }
                          variant="secondary"
                          onClick={() => setIsInternalSignatureDocumentsModalOpen(true)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <InternalSignatureDocumentsModal
                open={isInternalSignatureDocumentsModalOpen}
                handleClose={() => setIsInternalSignatureDocumentsModalOpen(false)}
                internalSignatureDocuments={internalSignatureDocuments}
                onChange={setInternalSignatureDocuments}
                creditApplicationFieldChoices={getTemplateFieldChoices(steps)}
              />
              <div className="flex flex-row mt-4">
                <DillButton
                  width="100px"
                  label="Submit"
                  onClick={handleSubmit}
                  isLoading={loading}
                  isDisabled={steps.length === 0 || loading}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col w-2/3 h-full px-4">
            <PreviewCreditApplicationTemplate stepsDetails={steps} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCreditApplicationTemplate;
